import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tree, message, Spin, Tag, Popconfirm } from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined, DeleteOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import { connect } from "react-redux";
import { api } from "@redux/api";
import { getAllPermissionKeys, getLeafPermissionKeys, PERMISSION_VALUES } from './utils';
import { permissionTree } from '@config/permissionTree';
import _ from "lodash";
import Auth from '@/components/Auth';
import { roleMiddleware } from './role-middleware';

const { Option } = Select;

const Role = ({ role: userRole }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false); // 新增查看弹窗状态
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null); // 新增查看记录状态
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [viewCheckedKeys, setViewCheckedKeys] = useState([]); // 新增查看模式的选中keys
  const [expandedKeys, setExpandedKeys] = useState([]); // 新增展开的keys状态
  const [viewExpandedKeys, setViewExpandedKeys] = useState([]); // 新增查看模式的展开keys状态
  const [isAllExpanded, setIsAllExpanded] = useState(false); // 新增是否全部展开的状态
  const [viewIsAllExpanded, setViewIsAllExpanded] = useState(false); // 新增查看模式是否全部展开的状态
  const [form] = Form.useForm();

  // 角色映射配置
  const roleConfig = {
    1: { label: 'Super User', color: 'red' },
    2.1: { label: 'Account Manager', color: 'orange' },
    2.2: { label: 'Portfolio Manager', color: 'blue' },
    3: { label: 'Company Admin', color: 'green' },
    4: { label: 'Standard User', color: 'default' },
  };

  // 表格列定义
  const columns = [
    {
      title: 'Profile Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Auth code="role_view_feature" fullback={text}>
          <Button
            type="link"
            style={{
              padding: 0,
              height: 'auto',
              color: '#1890ff',
              fontWeight: 'normal'
            }}
            onClick={() => handleView(record)}
          >
            {text}
          </Button>
        </Auth>

      ),
    },
    {
      title: 'Account Level',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (roleValue) => {
        const config = roleConfig[roleValue];
        if (config) {
          return (
            <Tag color={config.color}>
              {config.label}
            </Tag>
          );
        }
        return <Tag>{roleValue}</Tag>;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdby_name',
      key: 'createdby_name',
      width: 120,
    },
    {
      title: 'Modified By',
      dataIndex: 'lastModified_name',
      key: 'lastModified_name',
      width: 120,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Auth code="role_edit_feature">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={userRole !== '1.0'}
            >
              Edit
            </Button>
          </Auth>
          <Auth code="role_delete_feature">
            <Popconfirm
              title="Are you sure you want to delete this role?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          </Auth>
        </div>
      ),
    },
  ];

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.filterProfile({});
      setData(response.data.results || []);
    } catch (error) {
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchData();
  }, []);

  // 将API数据转换为Tree的checkedKeys（只检查叶子节点）
  const convertDataToCheckedKeys = (record) => {
    const checkedKeys = [];
    const leafPermissionKeys = getLeafPermissionKeys(); // 只获取叶子节点

    // 只检查叶子节点的权限值
    leafPermissionKeys.forEach(key => {
      const value = record[key];
      if (value === PERMISSION_VALUES.FULL_ACCESS) {
        checkedKeys.push(key);
      }
    });

    return checkedKeys;
  };

  // 将Tree的checkedKeys转换为API数据格式
  const convertCheckedKeysToData = (checkedKeys) => {
    const result = {};
    const allPermissionKeys = getAllPermissionKeys();

    // 初始化所有权限为0
    allPermissionKeys.forEach(key => {
      result[key] = PERMISSION_VALUES.DISABLED;
    });

    // 设置选中的权限为3
    checkedKeys.forEach(key => {
      result[key] = PERMISSION_VALUES.FULL_ACCESS;
    });

    // 处理父子关系：若任意子孙被选中，所有祖先都应设置为3
    const setParentPermissions = (nodes) => {
      const traverse = (nodeList) => {
        let subtreeSelected = false;

        nodeList.forEach(node => {
          const selfSelected = checkedKeys.includes(node.key);
          const hasChildren = node.children && node.children.length > 0;
          const childSelected = hasChildren ? traverse(node.children) : false;

          if (childSelected && !selfSelected) {
            result[node.key] = PERMISSION_VALUES.FULL_ACCESS;
          }

          if (selfSelected || childSelected) {
            subtreeSelected = true;
          }
        });

        return subtreeSelected;
      };

      traverse(nodes);
    };

    setParentPermissions(permissionTree);

    return result;
  };

  // 打开查看弹窗
  const handleView = (record) => {
    setViewingRecord(record);
    setViewModalVisible(true);

    // 设置权限树选中状态（只读模式）
    const initialCheckedKeys = convertDataToCheckedKeys(record);
    setViewCheckedKeys(initialCheckedKeys);
  };

  // 打开编辑弹窗
  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditModalVisible(true);

    // 设置表单初始值
    form.setFieldsValue({
      name: record.name,
      role: record.role,
      comment: record.comment,
    });

    // 设置权限树选中状态
    const initialCheckedKeys = convertDataToCheckedKeys(record);
    setCheckedKeys(initialCheckedKeys);
  };

  // 创建新角色
  const handleCreate = () => {
    setEditingRecord(null);
    setEditModalVisible(true);
    form.resetFields();
    setCheckedKeys([]);
  };

  // 删除角色
  const handleDelete = async (record) => {
    try {
      await api.deleteUserpermissions(record.id);
      message.success('Profile deleted successfully');
      fetchData(); // 刷新数据
    } catch (error) {
      message.error('Failed to delete, this profile is used by other users');
    }
  };

  // 保存角色
  const handleSave = async () => {
    const formValues = await form.validateFields();
    const permissionData = convertCheckedKeysToData(checkedKeys);
    debugger
    const saveData = {
      ...formValues,
      ...permissionData,
    };


    const processedData = roleMiddleware(saveData);

    if (editingRecord) {
      // 更新现有角色
      await api.editUserpermissions(editingRecord.id, processedData);
      message.success('Profile updated successfully');
    } else {
      // 创建新角色
      await api.createUserpermissions(processedData);
      message.success('Profile created successfully');
    }

    setEditModalVisible(false);
    fetchData(); // 刷新数据

  };

  // 关闭编辑弹窗
  const handleCancel = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
    setCheckedKeys([]);
    setExpandedKeys([]);
    setIsAllExpanded(false);
  };

  // 关闭查看弹窗
  const handleViewCancel = () => {
    setViewModalVisible(false);
    setViewingRecord(null);
    setViewCheckedKeys([]);
    setViewExpandedKeys([]);
    setViewIsAllExpanded(false);
  };

  // 获取所有可展开的节点keys
  const getAllExpandableKeys = (treeData) => {
    const keys = [];
    const traverse = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          keys.push(node.key);
          traverse(node.children);
        }
      });
    };
    traverse(treeData);
    return keys;
  };

  // 切换全部展开/收起
  const toggleExpandAll = (isViewMode = false) => {
    const allKeys = getAllExpandableKeys(permissionTree);
    
    if (isViewMode) {
      if (viewIsAllExpanded) {
        setViewExpandedKeys([]);
        setViewIsAllExpanded(false);
      } else {
        setViewExpandedKeys(allKeys);
        setViewIsAllExpanded(true);
      }
    } else {
      if (isAllExpanded) {
        setExpandedKeys([]);
        setIsAllExpanded(false);
      } else {
        setExpandedKeys(allKeys);
        setIsAllExpanded(true);
      }
    }
  };

  // Tree选中状态变化
  const onTreeCheck = (checkedKeysValue) => {
    setCheckedKeys(checkedKeysValue);
  };

  // Tree展开状态变化
  const onTreeExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
    setIsAllExpanded(expandedKeysValue.length === getAllExpandableKeys(permissionTree).length);
  };

  // 查看模式Tree展开状态变化
  const onViewTreeExpand = (expandedKeysValue) => {
    setViewExpandedKeys(expandedKeysValue);
    setViewIsAllExpanded(expandedKeysValue.length === getAllExpandableKeys(permissionTree).length);
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* 操作按钮区域 */}
      <div style={{ marginBottom: '16px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchData}
          style={{ marginRight: '8px' }}
        >
          Refresh
        </Button>
        <Auth code="role_create_feature">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            New Profile
          </Button>
        </Auth>
      </div>

      {/* 角色列表表格 */}
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
        }}
      />

      {/* 查看弹窗（只读模式） */}
      <Modal
        title={`View Profile: ${viewingRecord?.name || ''}`}
        open={viewModalVisible}
        onCancel={handleViewCancel}
        width={800}
        footer={[
          <Button key="close" onClick={handleViewCancel}>
            Close
          </Button>
        ]}
      >
        {viewingRecord && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Profile Name:</label>
              <div style={{ marginTop: '4px', fontSize: '14px' }}>{viewingRecord.name}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Account Level:</label>
              <div style={{ marginTop: '4px' }}>
                {roleConfig[viewingRecord.role] ? (
                  <Tag color={roleConfig[viewingRecord.role].color}>
                    {roleConfig[viewingRecord.role].label}
                  </Tag>
                ) : (
                  <Tag>{viewingRecord.role}</Tag>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Comment:</label>
              <div style={{ marginTop: '4px', fontSize: '14px' }}>
                {viewingRecord.comment || 'No comment'}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Permissions:</label>
              <div style={{ marginBottom: '8px' }}>
                <Button
                  type="link"
                  size="small"
                  icon={viewIsAllExpanded ? <DownOutlined /> : <RightOutlined />}
                  onClick={() => toggleExpandAll(true)}
                >
                  {viewIsAllExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              </div>
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                overflowY: 'auto',
                marginTop: '8px',
                backgroundColor: '#f9f9f9'
              }}>
                <Tree
                  checkable
                  disabled // 禁用所有操作，只读模式
                  treeData={permissionTree}
                  checkedKeys={viewCheckedKeys}
                  expandedKeys={viewExpandedKeys}
                  onExpand={onViewTreeExpand}
                  selectable={false}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 编辑/创建弹窗 */}
      <Modal
        title={editingRecord ? 'Edit Profile' : 'Create New Profile'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        width={800}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="name"
            label="Profile Name"
            rules={[{ required: true, message: 'Please enter role name' }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Account Level"
            rules={[{ required: true, message: 'Please select role level' }]}
          >
            <Select placeholder="Select role level">
              <Option value={1}>Super User</Option>
              <Option value={2.1}>Account Manager</Option>
              <Option value={2.2}>Portfolio Manager</Option>
              <Option value={3}>Company Admin</Option>
              <Option value={4}>Standard User</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="comment"
            label="Comment"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter comment (optional)"
            />
          </Form.Item>

          <Form.Item label="Permissions">
            <div style={{ marginBottom: '8px' }}>
              <Button
                type="link"
                size="small"
                icon={isAllExpanded ? <DownOutlined /> : <RightOutlined />}
                onClick={() => toggleExpandAll(false)}
              >
                {isAllExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            </div>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px', overflowY: 'auto' }}>
              <Tree
                checkable
                treeData={permissionTree}
                checkedKeys={checkedKeys}
                expandedKeys={expandedKeys}
                onCheck={onTreeCheck}
                onExpand={onTreeExpand}
                selectable={false}
              />
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    role: _.get(state, 'Auth.role') ? Object.keys(_.get(state, 'Auth.role'))[0] : null,
  }
}

export default connect(mapStateToProps)(Role);